import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, ChevronDown, Search, X } from "lucide-react";
import { VENEZUELAN_BANKS, getBankById } from "../utils/venezuelanBanks.js";

/**
 * Selector de bancos venezolanos con búsqueda e imagen.
 */
const BankSearchSelect = ({
  value = "",
  onChange,
  required = false,
  placeholder = "Buscar banco…",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const selected = getBankById(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return VENEZUELAN_BANKS;
    return VENEZUELAN_BANKS.filter((bank) => {
      const haystack = `${bank.name} ${bank.code} ${bank.id}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white text-left outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected?.image ? (
            <img
              src={selected.image}
              alt=""
              className="h-6 w-6 rounded object-contain bg-white shrink-0"
            />
          ) : (
            <Building2 size={18} className="text-gray-400 shrink-0" />
          )}
          <span
            className={`truncate ${
              selected ? "text-gray-800 font-medium" : "text-gray-400"
            }`}
          >
            {selected
              ? `${selected.name}${selected.code ? ` · ${selected.code}` : ""}`
              : placeholder}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-400 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Campo oculto para validación HTML required */}
      {required && (
        <input
          tabIndex={-1}
          className="sr-only"
          value={value || ""}
          onChange={() => {}}
          required
        />
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
            <Search size={14} className="text-gray-400 shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nombre o código…"
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-gray-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-sm text-gray-400 italic">
                Sin resultados
              </li>
            ) : (
              filtered.map((bank) => (
                <li key={bank.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(bank.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-indigo-50 ${
                      value === bank.id
                        ? "bg-indigo-50 text-indigo-700 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {bank.image ? (
                      <img
                        src={bank.image}
                        alt=""
                        className="h-7 w-7 rounded object-contain bg-white shrink-0 border border-gray-100"
                      />
                    ) : (
                      <span className="h-7 w-7 rounded bg-gray-100 flex items-center justify-center shrink-0">
                        <Building2 size={14} className="text-gray-400" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate">{bank.name}</span>
                      <span className="block text-[11px] text-gray-400">
                        {bank.code}
                      </span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BankSearchSelect;
